import React, { useEffect, useMemo, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { useHospitalData } from '../../hooks/useHospitalData'
import { usePersistedState } from '../../hooks/usePersistedState'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import NoDataOverlay from '../../components/Common/NoDataOverlay'
import StackedBarChart from '../../components/Charts/StackedBarChart'
import { parseMonthToValue } from '../../utils/dataUtils'
import { MonthInfo, generateMonthList } from '../../utils/coreUtils'
import { createChartExportHandlers, createMenuToggle, setupMenuClickOutside, exportToCSV } from '../../utils/exportUtils'
import './HuddleBoardPlan.css'

interface ZohoResponse {
    response?: {
        result?: {
            column_order?: string[]
            rows?: any[][]
        }
    }
}

// ── Q8 constants ──────────────────────────────────────────────────────────────

const Q8_QUESTION_TEXT = "Did the team discuss the care Plan options that incorporated or addressed the patient's Preferences or concerns?"
const Q8_QUESTION = (
    <>
        Did the team discuss the care Plan options that incorporated or addressed the patient's<br />
        Preferences or concerns?
    </>
)

const Q8_ANSWER_ORDER: string[] = ['Yes', 'No']

// ── Q9 constants ──────────────────────────────────────────────────────────────

const Q9_QUESTION_TEXT = "Were the following care plans written or updated on the shared planning board?"
const Q9_QUESTION = 'Were the following care plans written or updated on the shared planning board?'

const Q9_CHART_TYPES = ['Plan for Mom', 'Plan for Baby', 'Plan for Labor Progress'] as const
type Q9ChartType = typeof Q9_CHART_TYPES[number]

// Stacking order: bottom → top
const Q9_ANSWER_ORDER = ['Yes, Board Updated', 'No, Board left Blank', 'No, Board did not Need Updating'] as const
const Q9_LEGEND_ORDER = ['No, Board did not Need Updating', 'No, Board left Blank', 'Yes, Board Updated'] as const

const Q9_ANSWER_COLORS: Record<string, string> = {
    'Yes, Board Updated': '#2C5985',
    'No, Board left Blank': '#AEDCD5',
    'No, Board did not Need Updating': '#E48E43',
}
const Q9_CHART_COLORS = Q9_ANSWER_ORDER.map(a => Q9_ANSWER_COLORS[a])
const Q9_DATA_LABEL_COLORS = ['#ffffff', '#626262', '#ffffff']

// ── Shared constants ──────────────────────────────────────────────────────────

const Q8_MENU_ID = 'hdl-board-plan-q8-export-menu'
const Q9_MENU_ID = 'hdl-board-plan-q9-export-menu'
const HDL_SLIDER_KEY = 'hdl_slider'
const HDL_LAST_HOSPITAL_COMBO_KEY = 'hdl_last_hospital_combo'

// ── Helpers ───────────────────────────────────────────────────────────────────

const getSliderIndices = (monthList: MonthInfo[], range: [number, number]): [number, number] => {
    if (!monthList.length) return [0, 0]
    const maxIndex = monthList.length - 1
    const [startVal, endVal] = range
    if (!startVal || !endVal) return [0, maxIndex]

    let startIdx = monthList.findIndex(m => m.value === startVal)
    if (startIdx === -1) startIdx = monthList.findIndex(m => m.value >= startVal)
    if (startIdx === -1) startIdx = 0

    let endIdx = -1
    for (let i = maxIndex; i >= 0; i--) {
        if (monthList[i].value <= endVal) { endIdx = i; break }
    }
    if (endIdx === -1) endIdx = maxIndex

    startIdx = Math.max(0, Math.min(startIdx, maxIndex))
    endIdx = Math.max(0, Math.min(endIdx, maxIndex))
    if (startIdx > endIdx) return [0, maxIndex]
    return [startIdx, endIdx]
}

const normalizeQ8Answer = (raw: string): string | null => {
    const trimmed = raw.trim()
    if (!trimmed) return null
    const lower = trimmed.toLowerCase()
    if (lower.startsWith('yes')) return 'Yes'
    if (lower.startsWith('no')) return 'No'
    return null
}

const normalizeQ9Answer = (raw: string): string | null => {
    const lower = raw.trim().toLowerCase()
    if (!lower) return null
    if (lower.startsWith('yes')) return 'Yes, Board Updated'
    if (lower.includes('left blank') || (lower.startsWith('no') && lower.includes('blank'))) return 'No, Board left Blank'
    if (lower.includes('did not') || lower.includes('not need')) return 'No, Board did not Need Updating'
    return null
}

// ── Types ─────────────────────────────────────────────────────────────────────

type Q8Aggregated = {
    counts: Record<string, number>
    total: number
    percentages: Record<string, number>
    hospitalCount: number
}

type Q9ChartAgg = {
    counts: Record<string, number>
    percentages: Record<string, number>
    hospitalCount: number
    totalN: number
}

const EMPTY_Q8_AGG: Q8Aggregated = { counts: {}, total: 0, nCount: 0, percentages: {}, hospitalCount: 0 }

// ── Component ─────────────────────────────────────────────────────────────────

const HuddleBoardPlan: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect } = useHospitalSelection()

    const hospitalIds = useMemo(
        () => (Array.isArray(selectedHospitalIds) ? selectedHospitalIds : []),
        [selectedHospitalIds]
    )

    const { data, loading, initialLoading, error } = useHospitalData<ZohoResponse>(
        '/api/hdl/board_plan_care_options_q8',
        hospitalIds
    )

    const {
        data: q9Data,
        loading: q9Loading,
        initialLoading: q9InitialLoading,
        error: q9Error,
    } = useHospitalData<ZohoResponse>(
        '/api/hdl/board_plan_care_plans_written_q9',
        hospitalIds
    )

    const q8Ref = useRef<HTMLDivElement>(null)
    const q9Ref = useRef<HTMLDivElement>(null)

    const q8Exports = useMemo(
        () => createChartExportHandlers(
            q8Ref,
            'Q8',
            'huddle_board_plan_q8',
            {
                fontBoost: true,
                exportTitle: 'Discussion & Board Use: Plan — Q8',
                exportTitleAlign: 'center',
                extraHideSelectors: ['.chart-loading-overlay'],
            }
        ),
        []
    )
    const q9Exports = useMemo(
        () => createChartExportHandlers(
            q9Ref,
            'Q9',
            'huddle_board_plan_q9',
            {
                fontBoost: true,
                exportTitle: 'Discussion & Board Use: Plan — Q9',
                exportTitleAlign: 'center',
                extraHideSelectors: ['.chart-loading-overlay'],
            }
        ),
        []
    )

    const toggleQ8Menu = useMemo(() => createMenuToggle(Q8_MENU_ID), [])
    const toggleQ9Menu = useMemo(() => createMenuToggle(Q9_MENU_ID), [])

    useEffect(() => {
        return setupMenuClickOutside(() => {
            q8Exports.closeMenu(Q8_MENU_ID)
            q9Exports.closeMenu(Q9_MENU_ID)
        })
    }, [q8Exports, q9Exports])

    const rows: any[][] = data?.response?.result?.rows || []
    const q9Rows: any[][] = q9Data?.response?.result?.rows || []

    const monthList: MonthInfo[] = useMemo(() => {
        const allRows = [...rows, ...q9Rows]
        if (!allRows.length) return []
        return generateMonthList(allRows, 2)
    }, [rows, q9Rows])

    const hospitalIdsKey = hospitalIds.join('_')
    const persistenceKey = `${HDL_SLIDER_KEY}_${hospitalIdsKey}`
    const [sliderRangeValues, setSliderRangeValues] = usePersistedState<[number, number]>(
        persistenceKey,
        [0, 0]
    )

    const userInteractedRef = useRef(false)
    const initSeenRef = useRef(false)
    const lastDetectedKeyRef = useRef<string | null>(null)

    if (hospitalIds.length > 0 && lastDetectedKeyRef.current !== persistenceKey) {
        const isFirstInit = !initSeenRef.current
        lastDetectedKeyRef.current = persistenceKey
        initSeenRef.current = true

        if (isFirstInit) {
            let isSameComboAsLast = false
            try {
                const lastCombo = sessionStorage.getItem(HDL_LAST_HOSPITAL_COMBO_KEY)
                isSameComboAsLast = lastCombo === hospitalIdsKey
                sessionStorage.setItem(HDL_LAST_HOSPITAL_COMBO_KEY, hospitalIdsKey)
            } catch {}

            if (isSameComboAsLast) {
                try {
                    const item = sessionStorage.getItem(persistenceKey)
                    const parsed = item !== null ? JSON.parse(item) : null
                    userInteractedRef.current =
                        Array.isArray(parsed) && parsed[0] !== 0 && parsed[1] !== 0
                } catch {
                    userInteractedRef.current = false
                }
            } else {
                userInteractedRef.current = false
            }
        } else {
            userInteractedRef.current = false
            try {
                sessionStorage.setItem(HDL_LAST_HOSPITAL_COMBO_KEY, hospitalIdsKey)
            } catch {}
        }
    }

    const isLoadingData = loading || q9Loading

    useEffect(() => {
        if (!monthList.length) return
        if (isLoadingData) return
        const maxIdx = monthList.length - 1
        const fullRange: [number, number] = [monthList[0].value, monthList[maxIdx].value]
        const [curStart, curEnd] = sliderRangeValues

        if (!userInteractedRef.current) {
            if (curStart !== fullRange[0] || curEnd !== fullRange[1]) {
                setSliderRangeValues(fullRange)
            }
            return
        }

        const stillValid =
            curStart >= monthList[0].value &&
            curEnd <= monthList[maxIdx].value &&
            curStart <= curEnd
        if (!curStart || !curEnd || !stillValid) {
            userInteractedRef.current = false
            setSliderRangeValues(fullRange)
        }
    }, [hospitalIdsKey, monthList, isLoadingData, sliderRangeValues, setSliderRangeValues])

    const [startIdx, endIdx] = useMemo(
        () => getSliderIndices(monthList, sliderRangeValues),
        [monthList, sliderRangeValues]
    )

    // ── Q8 aggregation ────────────────────────────────────────────────────────

    const aggregateQ8Rows = (sourceRows: any[][]): Q8Aggregated => {
        if (!monthList.length || !sourceRows.length) return EMPTY_Q8_AGG

        const minVal = monthList[startIdx]?.value ?? 0
        const maxVal = monthList[endIdx]?.value ?? 0

        type HospEntry = { pcts: Record<string, number[]> }
        const perHospital: Record<string, HospEntry> = {}

        sourceRows.forEach(r => {
            const hospId = String(r[0])
            const monthVal = parseMonthToValue(r[2])
            if (!monthVal || monthVal < minVal || monthVal > maxVal) return
            const answer = normalizeQ8Answer(String(r[3] || ''))
            if (!answer) return
            const pct = parseFloat(String(r[6]))
            if (isNaN(pct)) return

            if (!perHospital[hospId]) perHospital[hospId] = { pcts: {} }
            if (!perHospital[hospId].pcts[answer]) perHospital[hospId].pcts[answer] = []
            perHospital[hospId].pcts[answer].push(pct)
        })

        const hospIds = Object.keys(perHospital)
        if (hospIds.length === 0) return EMPTY_Q8_AGG

        const percentages: Record<string, number> = {}
        const counts: Record<string, number> = {}

        for (const answer of Q8_ANSWER_ORDER) {
            const hospAvgs = hospIds
                .filter(id => (perHospital[id].pcts[answer] ?? []).length > 0)
                .map(id => {
                    const vals = perHospital[id].pcts[answer]
                    return vals.reduce((s, v) => s + v, 0) / vals.length
                })
            percentages[answer] = hospAvgs.length > 0
                ? hospAvgs.reduce((s, v) => s + v, 0) / hospAvgs.length
                : 0
            counts[answer] = hospAvgs.length
        }

        return { counts, total: hospIds.length, percentages, hospitalCount: hospIds.length }
    }

    const q8Aggregated = useMemo(
        () => aggregateQ8Rows(rows),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [rows, monthList, startIdx, endIdx, isMultiSelect]
    )

    const q8HasData = q8Aggregated.hospitalCount > 0

    // ── Q9 aggregation ────────────────────────────────────────────────────────

    const aggregateQ9Rows = (sourceRows: any[][]): Record<Q9ChartType, Q9ChartAgg> => {
        const result = {
            'Plan for Mom': { counts: {}, percentages: {}, hospitalCount: 0, totalN: 0 },
            'Plan for Baby': { counts: {}, percentages: {}, hospitalCount: 0, totalN: 0 },
            'Plan for Labor Progress': { counts: {}, percentages: {}, hospitalCount: 0, totalN: 0 },
        } as Record<Q9ChartType, Q9ChartAgg>

        if (!monthList.length || !sourceRows.length) return result

        const minVal = monthList[startIdx]?.value ?? 0
        const maxVal = monthList[endIdx]?.value ?? 0

        // perHospital[hospId][chartType] = { counts: { answer: numeratorSum }, total: denomSum }
        type HospChartEntry = { counts: Record<string, number>; total: number }
        const perHospital: Record<string, Record<string, HospChartEntry>> = {}
        const seenDenomGroups = new Set<string>() // dedup denom per hospId|month|chartType
        const nTracker: Record<string, Record<string, number>> = {
            'Plan for Mom': {},
            'Plan for Baby': {},
            'Plan for Labor Progress': {},
        }

        sourceRows.forEach(r => {
            const hospId = String(r[0])
            const monthVal = parseMonthToValue(r[2])
            if (!monthVal || monthVal < minVal || monthVal > maxVal) return
            const chartType = String(r[3] || '').trim() as Q9ChartType
            if (!Q9_CHART_TYPES.includes(chartType)) return
            const answer = normalizeQ9Answer(String(r[4] || ''))
            if (!answer) return
            const numerator = Number(r[5]) || 0
            const denom = Number(r[6]) || 0
            const nCount = Number(r[8]) || 0

            if (!perHospital[hospId]) perHospital[hospId] = {}
            if (!perHospital[hospId][chartType]) perHospital[hospId][chartType] = { counts: {}, total: 0 }
            const h = perHospital[hospId][chartType]

            h.counts[answer] = (h.counts[answer] || 0) + numerator
            const gk = `${hospId}|${monthVal}|${chartType}`
            if (!seenDenomGroups.has(gk)) { seenDenomGroups.add(gk); h.total += denom }

            const nKey = `${hospId}|${String(r[2])}`
            if (!(nKey in nTracker[chartType])) {
                nTracker[chartType][nKey] = nCount
            }
        })

        for (const chartType of Q9_CHART_TYPES) {
            const contributing = Object.entries(perHospital)
                .filter(([_, charts]) => charts[chartType] && charts[chartType].total > 0)
            if (contributing.length === 0) continue

            // Pool numerators and denominators across all contributing hospitals
            const counts: Record<string, number> = {}
            let total = 0
            for (const [, charts] of contributing) {
                for (const [a, c] of Object.entries(charts[chartType].counts)) {
                    counts[a] = (counts[a] || 0) + c
                }
                total += charts[chartType].total
            }

            const percentages: Record<string, number> = {}
            for (const answer of Q9_ANSWER_ORDER) {
                percentages[answer] = total > 0 ? ((counts[answer] || 0) / total) * 100 : 0
            }

            const totalN = Object.values(nTracker[chartType]).reduce((s, v) => s + v, 0)
            result[chartType] = { counts, percentages, hospitalCount: contributing.length, totalN }
        }

        return result
    }

    const q9Aggregated = useMemo(
        () => aggregateQ9Rows(q9Rows),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [q9Rows, monthList, startIdx, endIdx, isMultiSelect]
    )

    const roundedQ9Percentages = (agg: Q9ChartAgg): Record<string, number> => {
        const result: Record<string, number> = {}
        if (agg.hospitalCount <= 0) {
            Q9_ANSWER_ORDER.forEach(a => { result[a] = 0 })
            return result
        }
        Q9_ANSWER_ORDER.forEach(a => { result[a] = Math.round(agg.percentages[a] || 0) })
        return result
    }

    const buildQ9Series = (agg: Q9ChartAgg) => {
        const rounded = roundedQ9Percentages(agg)
        return Q9_ANSWER_ORDER.map(answer => ({ name: answer, data: [rounded[answer]] }))
    }

    const q9HasAnyData = Q9_CHART_TYPES.some(ct => q9Aggregated[ct].hospitalCount > 0)
    const q9TotalN = q9Aggregated['Plan for Mom'].totalN
        || q9Aggregated['Plan for Baby'].totalN
        || q9Aggregated['Plan for Labor Progress'].totalN

    // ── CSV export ────────────────────────────────────────────────────────────

    const getMonthRangeLabel = (): string => {
        const startLabel = monthList[startIdx]?.label || ''
        const endLabel = monthList[endIdx]?.label || ''
        return startLabel && endLabel ? `${startLabel} - ${endLabel}` : ''
    }

    const handleDownloadQ8CSV = () => {
        const hospitalIdsStr = hospitalIds.join(',')
        const monthRange = getMonthRangeLabel()

        const rowsOut: (string | number)[][] = Q8_ANSWER_ORDER.map(answer => {
            const percent = q8Aggregated.total > 0
                ? `${Math.round(q8Aggregated.percentages[answer] ?? 0)}%`
                : ''
            return [hospitalIdsStr, monthRange, Q8_QUESTION_TEXT, answer, percent]
        })

        exportToCSV({
            headers: ['Hospital ID(s)', 'Month Range', 'Question', 'Answer', 'Percentage'],
            rows: rowsOut,
            filename: 'huddle_board_plan_q8',
        })
    }

    const handleDownloadQ9CSV = () => {
        const hospitalIdsStr = hospitalIds.join(',')
        const monthRange = getMonthRangeLabel()

        const rowsOut: (string | number)[][] = []

        for (const chartType of Q9_CHART_TYPES) {
            const agg = q9Aggregated[chartType]
            const rounded = roundedQ9Percentages(agg)
            for (const answer of Q9_ANSWER_ORDER) {
                const count = agg.counts[answer] || 0
                const percent = agg.hospitalCount > 0 ? `${rounded[answer]}%` : ''
                rowsOut.push([hospitalIdsStr, monthRange, Q9_QUESTION_TEXT, chartType, answer, count, percent])
            }
            rowsOut.push([hospitalIdsStr, monthRange, Q9_QUESTION_TEXT, chartType, 'Total (N)', agg.totalN || 0, ''])
        }

        exportToCSV({
            headers: ['Hospital ID(s)', 'Month Range', 'Question', 'Plan Type', 'Answer', 'Count', 'Percentage'],
            rows: rowsOut,
            filename: 'huddle_board_plan_q9',
        })
    }

    // ── Q8 table renderer ─────────────────────────────────────────────────────

    const renderQ8Table = (agg: Q8Aggregated, hasData: boolean) => {
        if (!hasData) {
            return <div className="hdl-board-plan-empty">No data for the selected range</div>
        }
        return (
            <div className="hdl-plan-table-wrap">
                <table className="hdl-plan-table">
                    <tbody>
                        {Q8_ANSWER_ORDER.map(answer => (
                            <tr key={answer} className="hdl-plan-row-highlighted">
                                <td className="plan-response-cell">{answer}</td>
                                <td className="plan-pct-cell">{`${Math.round(agg.percentages[answer] ?? 0)}%`}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        )
    }

    // ── Derived state ─────────────────────────────────────────────────────────

    const showInitialLoading = (initialLoading && loading) || (q9InitialLoading && q9Loading)
    const isRefreshing = (!initialLoading && loading) || (!q9InitialLoading && q9Loading)
    const hasHospitals = hospitalIds.length > 0
    const combinedError = error || q9Error
    const noData = !initialLoading && !loading && !q9InitialLoading && !q9Loading && !monthList.length

    return (
        <div className="hdl-board-plan">
            <div className="hdl-page-header">
                <h1 className="hdl-page-title">Discussion & Board Use: Plan</h1>
                <div className="hdl-slider-wrap">
                    {monthList.length > 0 && (
                        <DateRangeSlider
                            monthList={monthList}
                            startIndex={startIdx}
                            endIndex={endIdx}
                            onChange={(s, e) => {
                                const sv = monthList[s]?.value || 0
                                const ev = monthList[e]?.value || 0
                                if (sv && ev) {
                                    userInteractedRef.current = true
                                    setSliderRangeValues([sv, ev])
                                }
                            }}
                        />
                    )}
                </div>
            </div>

            <div className={`hdl-card${hasHospitals && !combinedError && noData ? ' hdl-card-empty' : ''}`}>
                {showInitialLoading && <LoadingNotice title="Loading huddle board plan data…" />}

                {!hasHospitals && (
                    <div className="hdl-notice">Select at least one hospital to view data.</div>
                )}

                {hasHospitals && combinedError && (
                    <div className="hdl-notice hdl-notice-error">Failed to load data: {combinedError}</div>
                )}

                <NoDataOverlay visible={hasHospitals && !combinedError && noData} />

                {hasHospitals && !combinedError && !noData && (
                    <div className="hdl-board-plan-section">
                        <div className="hdl-board-plan-section-head-row">
                            <div className="hdl-board-plan-section-title">Q8 &amp; Q9</div>
                        </div>

                        {/* Q8 */}
                        <div className="hdl-board-plan-q8-section" ref={q8Ref}>
                            <div className="hdl-board-plan-question-row">
                                <div className="hdl-board-plan-question">{Q8_QUESTION}</div>
                                <div className="export-menu export-ignore">
                                    <button className="hamburger" onClick={toggleQ8Menu}>&#9776;</button>
                                    <div id={Q8_MENU_ID} className="export-dropdown">
                                        <div onClick={() => q8Exports.handleDownloadSVG(Q8_MENU_ID)}>Download SVG</div>
                                        <div onClick={() => q8Exports.handleDownloadPNG(Q8_MENU_ID)}>Download PNG</div>
                                        <div onClick={() => { handleDownloadQ8CSV(); q8Exports.closeMenu(Q8_MENU_ID) }}>Download CSV</div>
                                    </div>
                                </div>
                            </div>
                            <div className="hdl-board-plan-content">
                                <div className="hdl-board-plan-col">
                                    {renderQ8Table(q8Aggregated, q8HasData)}
                                </div>
                            </div>
                        </div>

                        {/* Q9 */}
                        <div className="hdl-board-plan-q9-section" ref={q9Ref}>
                            <div className="hdl-board-plan-question-row">
                                <div className="hdl-board-plan-q9-question">{Q9_QUESTION}</div>
                                <div className="export-menu export-ignore">
                                    <button className="hamburger" onClick={toggleQ9Menu}>&#9776;</button>
                                    <div id={Q9_MENU_ID} className="export-dropdown">
                                        <div onClick={() => q9Exports.handleDownloadSVG(Q9_MENU_ID)}>Download SVG</div>
                                        <div onClick={() => q9Exports.handleDownloadPNG(Q9_MENU_ID)}>Download PNG</div>
                                        <div onClick={() => { handleDownloadQ9CSV(); q9Exports.closeMenu(Q9_MENU_ID) }}>Download CSV</div>
                                    </div>
                                </div>
                            </div>

                            {q9HasAnyData ? (
                                <div className="hdl-board-plan-q9-charts">
                                    {/* Row 1: labels */}
                                    {Q9_CHART_TYPES.map(chartType => (
                                        <div key={`lbl-${chartType}`} className="hdl-board-plan-q9-chart-label">
                                            {chartType}
                                        </div>
                                    ))}
                                    <div aria-hidden="true" />

                                    {/* Row 2: charts + legend */}
                                    {Q9_CHART_TYPES.map(chartType => (
                                        <div key={`chart-${chartType}`} className="hdl-board-plan-q9-chart-stack">
                                            <StackedBarChart
                                                categories={['']}
                                                series={buildQ9Series(q9Aggregated[chartType])}
                                                colors={Q9_CHART_COLORS}
                                                height={450}
                                                columnWidth="100%"
                                                fontFamily="Poppins, 'Segoe UI', sans-serif"
                                                yAxisShow={false}
                                                xAxisShow={false}
                                                showGrid={false}
                                                sparkline={true}
                                                showDataLabels={true}
                                                dataLabelColors={Q9_DATA_LABEL_COLORS}
                                                dataLabelMinPercent={6}
                                                fillOpacity={1}
                                                tooltipFixed={true}
                                                exportFilename={`huddle_board_plan_q9_${chartType.replace(/\s+/g, '_').toLowerCase()}`}
                                            />
                                        </div>
                                    ))}
                                    <div className="hdl-board-plan-q9-legend">
                                        {Q9_LEGEND_ORDER.map(answer => (
                                            <div key={answer} className="hdl-board-plan-q9-legend-item">
                                                <span
                                                    className="hdl-board-plan-q9-legend-dot"
                                                    style={{ backgroundColor: Q9_ANSWER_COLORS[answer] }}
                                                />
                                                <span className="hdl-board-plan-q9-legend-label">{answer}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Row 3: N= */}
                                    {q9HasAnyData && (
                                        <div className="hdl-board-plan-q9-n-row">
                                            <span className="hdl-board-plan-q9-n-label">N=</span>
                                            {Q9_CHART_TYPES.map(ct => (
                                                <span key={`n-${ct}`} className="hdl-board-plan-q9-n-val">
                                                    {q9Aggregated[ct].hospitalCount > 0 ? q9Aggregated[ct].totalN : ''}
                                                </span>
                                            ))}
                                            <span aria-hidden="true" />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="hdl-board-plan-empty">No data for the selected range</div>
                            )}
                        </div>

                        {isRefreshing && (
                            <div className="chart-loading-overlay">
                                <div>Loading...</div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default HuddleBoardPlan
